import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema";
import type { QuoteResponse } from "~/shared/schemas/quote-schema";
import type { QuoteStateType } from "~/shared/types/quote-details-types";
import { getStartOfToday } from "~/shared/utils/date-helpers.ts";
import {
	formatDateToLocalISOString,
	formatDateToYYYYMMDD,
	stripPhoneFormatting,
} from "~/shared/utils/string-fns.ts";
import { normalizeHoursToApiFormat } from "~/shared/utils/time-utils.ts";

import type { SelectedAddressBookItems } from "../context/quote-address-context.tsx";
import { imperialMetricConverter } from "./imperial-metric-converter.ts";
import { normalizeCountryCode } from "./quote-location-utils.ts";

// Constants for default values
const DEFAULT_VALUES = {
	PO_NUMBERS: "",
	BL_NUMBER: "",
	BILLING_CUSTOMER_ID: null,
	SOURCE_ID: 5,
	STATUS_LEVEL: 1,
	HOURS_DEFAULT: "",
} as const;

// A blank or unparseable ship date must never block Save (#550629), but the API
// binds ShipmentDate to a non-nullable DateTime, so the payload still has to
// carry a real date. Fall back to today — the same substitution the quote
// context already applies when a loaded quote has a past ship date.
const resolveShipmentDate = (shipmentDate: string | Date): Date => {
	const parsed =
		shipmentDate instanceof Date ? shipmentDate : new Date(shipmentDate);
	return Number.isNaN(parsed.getTime()) ? getStartOfToday() : parsed;
};

type FormattedCommodity = {
	id: number;
	isHazmat: boolean;
	unNumber?: string | null;
	packingGroupId?: number | null;
	hazmatClassId?: number | null;
	properShippingName?: string | null;
	emergencyContactName?: string | null;
	emergencyContactPhone?: string | null;
	commodityId?: number;
	description?: string;
	quantity?: number;
	weight?: number | null;
	dimensionLength?: number | null;
	dimensionWidth?: number | null;
	dimensionHeight?: number | null;
	nmfc?: string;
	isStackable?: boolean;
	unitTypeId?: number;
	unitType?: unknown;
	freightClassId?: number;
	freightClass?: unknown;
	pieceCaseCount?: number | null;
	hazmatClass?: unknown;
	hazmatGroups?: unknown;
	hazmatDetail?: unknown;
};

const formatAccessorials = (quoteState: QuoteStateType) => {
	return [
		...quoteState.deliveryServices,
		...quoteState.pickupServices,
		...quoteState.addtDetails,
	]
		.map((acc) => ({ id: acc.id, description: acc.name }))
		.filter((acc) => acc.id > 0);
};

const formatHazmatDetail = (
	item: QuoteStateType["items"][number],
	quoteState: QuoteStateType,
	includeHazmat = false,
) => {
	if (!includeHazmat || !item.isHazmat) return {};

	let emergencyContactPhone: string | null = null;

	if (item.isHazmat) {
		const phone =
			quoteState.emergencyContactPhone || item.emergencyContactPhone;
		// Always strip the display mask: phones are formatted for display at
		// hydration, so sending the raw field would let a bulk-uploaded number go
		// out re-formatted. Digits-only is the representation these APIs expect.
		const normalizedPhone = phone ? stripPhoneFormatting(phone) : phone;
		// Always send null (never an empty string) when no phone was entered so
		// the backend's phone-format validation is skipped for blank values.
		emergencyContactPhone = normalizedPhone || null;
	}

	return {
		unNumber: item.isHazmat ? (item.unNumber ?? null) : null,
		packingGroupId:
			item.isHazmat && item.packingGroupId?.id
				? Number(item.packingGroupId.id)
				: null,
		hazmatClassId:
			item.isHazmat && item.hazmatClass?.id
				? Number(item.hazmatClass.id)
				: null,
		properShippingName: item.isHazmat
			? (item.properShippingName ?? null)
			: null,
		emergencyContactName: item.isHazmat
			? quoteState.emergencyContactName || item.emergencyContactName || null
			: null,
		emergencyContactPhone,
		hazmatClass: item.hazmatClass ?? null,
		hazmatGroups: item.packingGroupId ?? null,
	};
};

const formatCommodities = (
	quoteState: QuoteStateType,
	isNewQuote = false,
	includeHazmat = false,
	isUpdate = false,
) => {
	return quoteState.items.map((item) => {
		if (isUpdate) {
			return {
				id: item.id,
				description: item.description,
				nmfc: item.nmfc ?? null,
				pieceCaseCount:
					item.pieceCount != null && item.pieceCount > 0
						? Number(item.pieceCount)
						: null,
				isHazmat: item.isHazmat,
				...formatHazmatDetail(item, quoteState, includeHazmat),
			};
		}

		// Ensure values are in imperial units
		const isMetric = quoteState.measuringType === "KG/CM";
		const converted = isMetric
			? imperialMetricConverter("LB/IN", {
					weight: item.weight ?? 0,
					length: item.length,
					width: item.width,
					height: item.height,
				})
			: {
					weight: item.weight,
					length: item.length,
					width: item.width,
					height: item.height,
				};

		return {
			id: isNewQuote ? 0 : item.id,
			commodityId: isNewQuote ? 0 : item.id,
			description: item.description,
			quantity: item.quantity,
			weight: item.weight == null ? null : Math.round(converted.weight ?? 0),

			dimensionLength:
				converted.length == null ? null : Math.round(converted.length),

			dimensionWidth:
				converted.width == null ? null : Math.round(converted.width),

			dimensionHeight:
				converted.height == null ? null : Math.round(converted.height),
			nmfc: item.nmfc,
			isHazmat: item.isHazmat,
			isStackable: item.isStackable,
			unitTypeId: Number(item.unit.id),
			unitType: item.unit,
			freightClassId: Number(item.class.id),
			freightClass: item.class,
			pieceCaseCount:
				item.pieceCount != null && item.pieceCount > 0
					? Number(item.pieceCount)
					: null,
			...formatHazmatDetail(item, quoteState, includeHazmat),
		};
	});
};

export const formatAddressDetails = (
	address: AddressBookItemSummary,
	addressType: "pickup" | "delivery",
	quoteState: QuoteStateType,
) => {
	// Contact details are in the details array, filtered by stopType (1 = pickup, 2 = delivery)
	const targetStopType = addressType === "pickup" ? 1 : 2;
	const contactDetails = address.details.find(
		(d) => d.stopType === targetStopType,
	);

	const baseAddress = {
		stopName: address.companyName,
		contactName: contactDetails?.contactName ?? null,
		// Phone numbers are formatted for display at hydration, so strip that
		// formatting on the way out. This keeps the payload byte-identical whether
		// the number came from a bulk upload, was hydrated, or was typed behind
		// the mask, and matches the digits-only form the APIs already expect.
		contactPhone: contactDetails?.contactPhone
			? stripPhoneFormatting(contactDetails.contactPhone)
			: (contactDetails?.contactPhone ?? null),
		contactExtension: contactDetails?.contactExtension ?? null,
		address1: address.address1,
		address2: address.address2 ?? null,
		hoursOpen: normalizeHoursToApiFormat(
			contactDetails?.hoursOpen ?? DEFAULT_VALUES.HOURS_DEFAULT,
		),
		hoursClose: normalizeHoursToApiFormat(
			contactDetails?.hoursClose ?? DEFAULT_VALUES.HOURS_DEFAULT,
		),
	};

	return addressType === "pickup"
		? {
				...baseAddress,
				puNumber: quoteState.puNumber,
			}
		: {
				...baseAddress,
				deliveryPO: quoteState.deliveryPO,
			};
};

const hasAddressDetails = (
	address: AddressBookItemSummary | null,
	addressType: "pickup" | "delivery",
	quoteState: QuoteStateType,
): address is AddressBookItemSummary => {
	if (!address) return false;

	const targetStopType = addressType === "pickup" ? 1 : 2;
	const contactDetails = address.details.find(
		(detail) => detail.stopType === targetStopType,
	);
	const referenceNumber =
		addressType === "pickup" ? quoteState.puNumber : quoteState.deliveryPO;

	return [
		address.companyName,
		address.address1,
		address.address2,
		contactDetails?.contactName,
		contactDetails?.contactPhone,
		contactDetails?.contactExtension,
		contactDetails?.hoursOpen,
		contactDetails?.hoursClose,
		referenceNumber,
	].some((value) => value?.trim());
};

const formatStopDetails = (
	address: AddressBookItemSummary | null,
	addressType: "pickup" | "delivery",
	quoteState: QuoteStateType,
	includePartialStopDetails: boolean,
) => {
	if (!address) return null;

	const shouldInclude = includePartialStopDetails
		? hasAddressDetails(address, addressType, quoteState)
		: Boolean(address.address1);

	return shouldInclude
		? formatAddressDetails(address, addressType, quoteState)
		: null;
};

// Shared base formatter that both functions can use
const formatQuoteBase = (
	quoteState: QuoteStateType,
	includeHazmat = false,
	isUpdate = false,
) => {
	return {
		id: quoteState.id || 0,
		eTag: quoteState.eTag,
		quoteCommodities: formatCommodities(
			quoteState,
			false,
			includeHazmat,
			isUpdate,
		) as FormattedCommodity[],
		shipmentDate: formatDateToLocalISOString(
			resolveShipmentDate(quoteState.shipmentDate),
		),
		customerReferences: quoteState.customerReference,
		soNumbers: quoteState.soNumber,
		specialInstructions: quoteState.specialInstructions ?? "",
	};
};

const getPieceCount = (items: QuoteStateType["items"]) => {
	return items.reduce((total, item) => total + (item.pieceCount ?? 0), 0);
};

const getLinearFeet = (
	linearFeet: number,
	customTotalLinearFeet: number | null,
) => {
	if (customTotalLinearFeet != null && customTotalLinearFeet > 0) {
		return customTotalLinearFeet.toString();
	}
	return linearFeet != null && !Number.isNaN(Number(linearFeet))
		? linearFeet.toString()
		: "0";
};

const formatQuoteStateBase = (
	quoteState: QuoteStateType,
	addresses: SelectedAddressBookItems,
	isNewQuote: boolean,
	isInternal = false,
	includePartialStopDetails = false,
) => {
	const accessorials = formatAccessorials(quoteState);
	const commodities = formatCommodities(
		quoteState,
		isNewQuote,
		false,
	) as FormattedCommodity[];
	const baseQuote = formatQuoteBase(quoteState, false);

	// Use address book items for origin/destination if available, otherwise fall back to quoteState
	const pickupAddress = addresses.pickup;
	const deliveryAddress = addresses.delivery;
	const pickupCountryRaw =
		pickupAddress?.country == ""
			? quoteState.pickupCountry.abbreviation
			: pickupAddress?.country;
	const deliveryCountryRaw =
		deliveryAddress?.country == ""
			? quoteState.deliveryCountry.abbreviation
			: deliveryAddress?.country;

	return {
		...baseQuote,
		id: isNewQuote ? 0 : quoteState.id || 0,
		statusLevel: DEFAULT_VALUES.STATUS_LEVEL,
		eTag: quoteState.eTag || "",
		origin: {
			name: "",
			postalCode:
				pickupAddress?.postalCode == ""
					? quoteState.pickupPostalCode.postalCode
					: pickupAddress?.postalCode,
			city:
				pickupAddress?.city == ""
					? quoteState.pickupPostalCode?.city?.name
					: pickupAddress?.city,
			state:
				pickupAddress?.stateProvince == ""
					? quoteState.pickupPostalCode?.city?.state?.abbreviation
					: pickupAddress?.stateProvince,
			country: normalizeCountryCode(pickupCountryRaw),
			address1: "",
			address2: "",
		},
		destination: {
			name: "",
			postalCode:
				deliveryAddress?.postalCode == ""
					? quoteState.deliveryPostalCode.postalCode
					: deliveryAddress?.postalCode,
			city:
				deliveryAddress?.city == ""
					? quoteState.deliveryPostalCode?.city?.name
					: deliveryAddress?.city,
			state:
				deliveryAddress?.stateProvince == ""
					? quoteState.deliveryPostalCode?.city?.state?.abbreviation
					: deliveryAddress?.stateProvince,
			country: normalizeCountryCode(deliveryCountryRaw),
			address1: "",
			address2: "",
		},
		isGuest: false,
		accessorials: accessorials,
		pieceCaseCount: getPieceCount(quoteState.items) || null,
		carrierPrices: [],
		quotePreviousRate: quoteState.quotePreviousRate ?? null,
		selectedCarrierPriceId: null,
		insuranceApplyType: quoteState.insuranceApplyType ?? null,
		insuranceApplyTypeId: quoteState.insuranceApplyTypeId,
		insuranceProductType: quoteState.insuranceProductType ?? null,
		insuranceProductTypeId: quoteState.insuranceProductTypeId,
		insuranceValue: quoteState.insuranceValue,
		//dupes?
		poNumbers: quoteState.poNumbers,
		poNumber: quoteState.poNumber == null ? null : String(quoteState.poNumber),
		createdByDistributorId: null,
		showBLNumber: false,
		templateId: quoteState.templateId ?? null,
		customer: {
			id: quoteState.customer.id,
			name: quoteState.customer.name,
		},
		customerReferences: quoteState.customerReference,
		shipmentDate: formatDateToYYYYMMDD(
			resolveShipmentDate(quoteState.shipmentDate),
		),
		customerId: quoteState.customer.id,
		quoteCommodities: commodities,
		totalLinearFeet: getLinearFeet(
			quoteState.totalLinearFeet,
			quoteState.customTotalLinearFeet,
		),
		sourceId: isInternal ? 5 : 6,
		publicGuid: crypto.randomUUID(),
		deliveryDetails: formatStopDetails(
			deliveryAddress,
			"delivery",
			quoteState,
			includePartialStopDetails,
		),
		pickupDetails: formatStopDetails(
			pickupAddress,
			"pickup",
			quoteState,
			includePartialStopDetails,
		),
		specialInstructions: quoteState.specialInstructions ?? "",
	};
};

export const formatQuoteState = (
	quoteState: QuoteStateType,
	addresses: SelectedAddressBookItems,
	isNewQuote: boolean,
	isInternal = false,
) => formatQuoteStateBase(quoteState, addresses, isNewQuote, isInternal);

export const formatQuoteStateForTemplate = (
	quoteState: QuoteStateType,
	addresses: SelectedAddressBookItems,
) => formatQuoteStateBase(quoteState, addresses, true, false, true);

export const formatSpecialInstructions = (
	addresses: SelectedAddressBookItems,
) => {
	let specialInstructions = "";
	if (addresses.pickup) {
		const pickupDetail = addresses.pickup.details.find((d) => d.stopType === 1);
		specialInstructions += pickupDetail?.specialInstructions ?? "";
	}
	if (addresses.delivery) {
		const deliveryDetail = addresses.delivery.details.find(
			(d) => d.stopType === 2,
		);
		if (specialInstructions != "") specialInstructions += "\n";
		specialInstructions += deliveryDetail?.specialInstructions ?? "";
	}
	return specialInstructions;
};

export const formatQuoteForUpdate = (
	quoteState: QuoteStateType,
	addresses: SelectedAddressBookItems,
) => {
	const baseQuote = formatQuoteBase(quoteState, true, true);
	return {
		...baseQuote,
		quoteId: quoteState.id,
		poNumbers: quoteState.poNumbers,
		blNumber: DEFAULT_VALUES.BL_NUMBER,
		billingCustomerId: quoteState.billingCustomerId ?? quoteState.customer.id,
		pickupDetails: addresses.pickup
			? formatAddressDetails(addresses.pickup, "pickup", quoteState)
			: null,
		deliveryDetails: addresses.delivery
			? formatAddressDetails(addresses.delivery, "delivery", quoteState)
			: null,
		insuranceValue: quoteState.insuranceValue ?? null,
		quoteCommodities: baseQuote.quoteCommodities.map((item) => {
			const commodity = {
				...item,
				commodityId: item.id,
			};
			const limitedUpdateFields = new Set([
				"commodityId",
				"description",
				"unNumber",
				"packingGroupId",
				"hazmatClassId",
				"properShippingName",
				"emergencyContactName",
				"emergencyContactPhone",
				"nmfc",
				"pieceCaseCount",
			]);

			// Strip everything except the allowed fields for update
			for (const key of Object.keys(commodity)) {
				if (!limitedUpdateFields.has(key)) {
					delete commodity[key as keyof FormattedCommodity];
				}
			}
			return commodity;
		}),
	};
};

export const formatQuoteForTender = (quoteState: QuoteResponse) => {
	return {
		...quoteState,
		origin: {
			...quoteState.origin,
			name: "",
			address1: "",
			address2: "",
		},
		destination: {
			...quoteState.destination,
			name: "",
			address1: "",
			address2: "",
		},
		carrierPrices: (
			quoteState.carrierPrices as {
				[key: string]: unknown;
				generatedPartialsRate?: unknown;
			}[]
		).map((carr) => ({
			...carr,
			generatedPartialsRate: Number(
				(carr as { generatedPartialsRate?: unknown }).generatedPartialsRate,
			),
		})),
		createdByUserId: quoteState.createdByUserId ?? 0,
		createdByEmail: quoteState.createdByEmail ?? "",
		poNumber: quoteState.poNumber ?? 0,
	};
};
